import {
  CustomFilterOption,
  FilterItem,
  FilterSelectedValues,
} from '@/common/components/molecules/filterSearch/filterSearch.type';
import { computed, ComputedRef, ref } from 'vue';
import {
  filterFunctionByOperator,
  getSpecificFilterCellValue,
  getFilterableValue,
} from '@/common/components/molecules/filterSearch/filterSearch.utils';
import { CustomColumn } from '@/common/utils';
import {
  GridRowInstance,
  GridColumnInstance,
  GridRawRowDataType,
} from '@/common/components/molecules/modernGrid/modernGrid.types';
import { FILTER_CONDITION_BY_SEARCH_TYPE } from '@/common/components/molecules/filterSearch/filterSearch.specificFilterFunctions';
import { isNil } from 'lodash-es';
import { QuickFilterSelectedValues } from '../quickFilter/quickFilter.types';

export const useEvGridFilterSearch = <Row extends Record<string, any>>({
  columns,
  filterItems,
}: {
  columns: ComputedRef<CustomColumn[]>;
  filterItems: FilterItem[];
}) => {
  const filterSearchResultMV = ref<FilterSelectedValues>();

  const customFilterFnByKey: { [key: string]: FilterItem['customFilterFn'] } = filterItems.reduce(
    (acc, item) => {
      acc[item.key.id] = item?.customFilterFn;
      return acc;
    },
    {} as { [key: string]: FilterItem['customFilterFn'] },
  );

  const specificFilterFnMap = computed(() => {
    return (
      columns.value?.reduce(
        (acc, item) => {
          const filterFn = FILTER_CONDITION_BY_SEARCH_TYPE[item?.specificFilterFnKey ?? ''];
          if (filterFn) {
            acc[item.field] = filterFn;
          }
          return acc;
        },
        {} as { [key: string]: FilterItem['customFilterFn'] },
      ) ?? {}
    );
  });

  const getCellValue = ({
    tokenKey,
    row,
    isTreeGrid,
  }: {
    tokenKey: string;
    row: Row;
    isTreeGrid: boolean;
  }) => {
    const targetColumnIndex = columns?.value?.findIndex((column) => column.field === tokenKey);
    if (targetColumnIndex < 0) {
      return undefined;
    }

    const column = columns?.value?.[targetColumnIndex];
    if (column?.rendererType === 'toSec') {
      const unit = column?.attrs?.unit || 'ms';
      return isTreeGrid ? `${row[tokenKey]} ${unit}` : `${row[targetColumnIndex]} ${unit}`;
    }
    return isTreeGrid ? row[tokenKey] : row[targetColumnIndex];
  };

  const commonFilterCondition = (
    row: Row,
    token: {
      key: string;
      operator: string;
      value: string;
    },
    isTreeGrid: boolean,
  ): boolean => {
    const targetColumnIndex = columns?.value?.findIndex((column) => column.field === token.key);
    if (targetColumnIndex < 0) {
      return false;
    }

    const { type } = columns.value[targetColumnIndex];
    const multi = filterItems?.find((item) => item.key.id === token.key)?.values?.multi;
    const searchValues: string[] = multi ? token.value.split(',') : [token.value];
    const targetValue = getCellValue({ tokenKey: token.key, row, isTreeGrid });
    // Record 객체(multi-chip-cell 등)는 Object.keys()를 필터 대상으로 사용
    const filterableValue =
      typeof targetValue === 'object' && targetValue !== null && !Array.isArray(targetValue)
        ? Object.keys(targetValue)
        : targetValue;

    return filterFunctionByOperator({
      targetValue: filterableValue,
      operator: token.operator,
      searchValues,
      type,
    });
  };

  // externalFilterFnOpt : 외부에서 추가된 filter function을 포함한 옵션
  // internalFilterFn : FilterItems에서 받은 filter function
  // specificFilterFn : FilterItems에서 정의한 specificFilterKey에 따른 filter function
  const evaluateToken = ({
    row,
    token,
    isTreeGrid,
    externalCustomOption,
  }: {
    row: any;
    token: any;
    isTreeGrid: boolean;
    externalCustomOption: CustomFilterOption[] | undefined;
  }): boolean => {
    const externalFilterFnOpt = externalCustomOption?.find((option) => option.id === token.key);
    if (externalFilterFnOpt) {
      return externalFilterFnOpt.filterFn(row, token);
    }

    const internalFilterFn: FilterItem['customFilterFn'] | undefined =
      customFilterFnByKey[token.key];
    if (internalFilterFn) {
      const cellValue = getCellValue({
        tokenKey: token.key,
        row,
        isTreeGrid,
      });

      if (isNil(cellValue)) {
        return false;
      }

      return internalFilterFn(cellValue, token);
    }

    const specificFilterFn: FilterItem['customFilterFn'] | undefined =
      specificFilterFnMap.value?.[token.key];
    if (specificFilterFn) {
      const cellValue = getCellValue({
        tokenKey: token.key,
        row,
        isTreeGrid,
      });

      if (isNil(cellValue)) {
        return false;
      }

      return specificFilterFn(cellValue, token);
    }

    return commonFilterCondition(row, token, isTreeGrid);
  };

  const filterGridByFilterSearch = (
    rows,
    result: FilterSelectedValues | undefined,
    externalCustomOption?: CustomFilterOption[],
  ): any[] => {
    if (!rows || !result?.tokens?.length) {
      return rows ?? [];
    }

    return rows?.filter((row) => {
      if (result.logicalOperator === 'AND') {
        return result.tokens.every((token) =>
          evaluateToken({
            row,
            token,
            externalCustomOption,
            isTreeGrid: false,
          }),
        );
      }

      return result.tokens.some((token) =>
        evaluateToken({
          row,
          token,
          externalCustomOption,
          isTreeGrid: false,
        }),
      );
    });
  };

  const showAllDescendants = (children, isParentExpand: boolean) => {
    children.forEach((child) => {
      child.show = isParentExpand;
      child.isFilter = true;
      if (child.children?.length) {
        showAllDescendants(child.children, isParentExpand && child.expand);
      }
    });
  };

  const filterTree = (
    rows,
    filterValues: FilterSelectedValues,
    parentExpand = true,
    externalCustomOption?: CustomFilterOption[],
  ): void => {
    rows.forEach((node) => {
      const isMatchedNode =
        filterValues?.logicalOperator === 'AND'
          ? filterValues.tokens.every((token) =>
              evaluateToken({
                row: node,
                token,
                externalCustomOption,
                isTreeGrid: true,
              }),
            )
          : filterValues.tokens.some((token) =>
              evaluateToken({
                row: node,
                token,
                externalCustomOption,
                isTreeGrid: true,
              }),
            );

      if (node.children) {
        filterTree(node.children, filterValues, parentExpand && node.expand);
      }

      // 자식 노드 중 하나라도 조건에 맞는 node가 있을 경우 부모 노드 show 처리
      const isIncludeNotFilteredChild = !!(node.children ?? []).some((child) => child.show);
      node.show = isMatchedNode || isIncludeNotFilteredChild;
      node.isFilter = node.show;

      if (!node.children?.length) return;

      if (isMatchedNode) {
        // 부모 노드가 show인 경우, 모든 자손 show 처리 (재귀)
        showAllDescendants(node.children, parentExpand && node.expand);
      } else {
        // 부모 노드가 show가 아닌 경우, 조건에 맞는 자식만 show 처리
        const isExpand = parentExpand && node.expand;
        node.children.forEach((child) => {
          child.show = isExpand && child.isFilter;
        });
      }
    });
  };

  const initTree = (rows) => {
    rows.forEach((node) => {
      const show = node.parent ? node.parent.expand && node.parent.show : true;

      node.show = show;
      node.isFilter = true;

      if (node.children?.length) {
        initTree(node.children);
      }
    });
  };

  function filterTreeGridByFilterSearch<T extends Record<string, any>>(
    rows: T[],
    result: FilterSelectedValues | undefined,
  ): T[] {
    function extractVisibleNodes(nodes: T[]): T[] {
      return nodes
        .filter((node) => node.show !== false)
        .map((node) => {
          if (node.children?.length) {
            return {
              ...node,
              children: extractVisibleNodes(node.children as T[]),
            } as T;
          }
          return node;
        });
    }

    initTree(rows);
    if (result?.tokens.length) {
      filterTree(rows, result);
    }

    return extractVisibleNodes(rows);
  }

  const changeStateObjectRowsByFilterSearch = (rows, result: FilterSelectedValues | undefined) => {
    if (!result?.tokens.length) {
      return rows.map((node) => ({ ...node, searched: false }));
    }

    return rows.map((node) => {
      const searched =
        result?.logicalOperator === 'AND'
          ? result.tokens.every((token) => commonFilterCondition(node, token, true))
          : result?.tokens.some((token) => commonFilterCondition(node, token, true));

      return {
        ...node,
        searched,
      };
    });
  };

  return {
    filterSearchResultMV,
    filterGridByFilterSearch,
    filterTreeGridByFilterSearch,
    changeStateObjectRowsByFilterSearch,
  };
};

export const useModernGridFilterSearch = <RowDataType extends GridRawRowDataType>({
  columns,
  filterItems,
}: {
  columns: ComputedRef<GridColumnInstance<RowDataType>[] | undefined>;
  filterItems: FilterItem[];
}) => {
  const filterSearchResultMV = ref<FilterSelectedValues>();
  const quickFilterResultMV = ref<QuickFilterSelectedValues>();

  const commonFilterCondition = (
    row: GridRowInstance<RowDataType>,
    token: {
      key: string;
      operator: string;
      value: string;
    },
  ): boolean => {
    const column = columns.value?.find(({ id }) => token.key === id);
    if (!column) return false;

    const { type = 'string', filterType } = column.columnDef.meta || {};
    const multi = filterItems?.find((item) => item.key.id === token.key)?.values?.multi;
    const searchValues: string[] = multi ? token.value.split(',') : [token.value];
    const rawValue = row.original[token.key];
    // Record 객체(multi-chip-cell 등)는 Object.keys()를 필터 대상으로 사용
    const isRecordValue =
      typeof rawValue === 'object' && rawValue !== null && !Array.isArray(rawValue);
    const filterableRawValue = isRecordValue ? Object.keys(rawValue) : rawValue;
    const formattedValue =
      getFilterableValue(row.getValue(token.key)) || getFilterableValue(rawValue);

    const rawFilterResult = filterFunctionByOperator({
      targetValue: filterableRawValue,
      operator: token.operator,
      searchValues,
      type,
    });

    const formattedFilterResult = filterFunctionByOperator({
      targetValue: formattedValue,
      operator: token.operator,
      searchValues,
      type,
    });

    let filterResult = formattedFilterResult;
    if (filterType === 'onlyRaw' || Array.isArray(filterableRawValue)) {
      filterResult = rawFilterResult;
    } else if (filterType === 'both') {
      filterResult = rawFilterResult || formattedFilterResult;
    }
    return filterResult;
  };

  const customFilterFnByKey: { [key: string]: FilterItem['customFilterFn'] } = filterItems.reduce(
    (acc, item) => {
      acc[item.key.id] = item?.customFilterFn;
      return acc;
    },
    {} as { [key: string]: FilterItem['customFilterFn'] },
  );

  const specificFilterFnMap = computed(() => {
    return (
      columns.value?.reduce(
        (acc, item) => {
          const filterFn =
            FILTER_CONDITION_BY_SEARCH_TYPE[item.columnDef.meta?.specificFilterFnKey ?? ''];
          if (filterFn) {
            acc[item.id] = filterFn;
          }
          return acc;
        },
        {} as { [key: string]: FilterItem['customFilterFn'] },
      ) ?? {}
    );
  });

  // externalFilterFnOpt : 외부에서 추가된 filter function을 포함한 옵션
  // internalFilterFn : FilterItems에서 받은 filter function
  // specificFilterFn : FilterItems에서 정의한 specificFilterKey에 따른 filter function
  const evaluateToken = ({
    row,
    token,
    externalCustomOption,
  }: {
    row: GridRowInstance<RowDataType>;
    token: {
      key: string;
      operator: string;
      value: string;
    };
    externalCustomOption: CustomFilterOption[] | undefined;
  }): boolean => {
    const externalFilterFnOpt = externalCustomOption?.find((option) => option.id === token.key);
    if (externalFilterFnOpt) {
      return externalFilterFnOpt.filterFn(row.original, token);
    }

    const internalFilterFn: FilterItem['customFilterFn'] | undefined =
      customFilterFnByKey[token.key];
    if (internalFilterFn) {
      const cellValue = getFilterableValue(row.original[token.key]);

      if (isNil(cellValue)) {
        return false;
      }

      return internalFilterFn(cellValue, token);
    }

    const specificFilterFn: FilterItem['customFilterFn'] | undefined =
      specificFilterFnMap.value?.[token.key];
    if (specificFilterFn) {
      const column = columns.value?.find(({ id }) => token.key === id);
      const cellValue = getSpecificFilterCellValue(
        getFilterableValue(row.original[token.key]),
        column?.columnDef.meta,
      );

      if (isNil(cellValue)) {
        return false;
      }

      return specificFilterFn(cellValue, token);
    }

    return commonFilterCondition(row, token);
  };

  const filterGridByFilterSearch = (
    row: GridRowInstance<RowDataType>,
    filterValue: FilterSelectedValues | QuickFilterSelectedValues | undefined,
    externalCustomOption?: CustomFilterOption[],
  ): boolean => {
    const isValidFilter = typeof filterValue === 'object' && filterValue !== null;
    const { tokens, logicalOperator } = filterValue || {};
    if (isValidFilter && tokens?.length) {
      return logicalOperator === 'AND'
        ? // @ts-ignore
          tokens.every((token) => evaluateToken({ row, token, externalCustomOption }))
        : tokens.some((token) => evaluateToken({ row, token, externalCustomOption }));
    }
    return true;
  };

  return {
    filterSearchResultMV,
    quickFilterResultMV,
    filterGridByFilterSearch,
  };
};
